import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';
import { DashboardLayout } from './Dashboard';
import { Check, Crown, Rocket, Star, Eye } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_META = {
  free: { name: 'Starter', icon: Star, color: '#9CA3AF', monthly: 0, currency: '€' },
  pro_trial: { name: 'Pro Trial', icon: Rocket, color: '#10B981', monthly: 0, currency: '€' },
  pro: { name: 'Pro', icon: Rocket, color: '#2563EB', monthly: 27, currency: '€', recommended: true, trial_days: 7 },
  agency: { name: 'Agency', icon: Crown, color: '#F59E0B', monthly: 97, currency: '€', trial_days: 7 },
};

const PlanCard = ({ planId, currentPlan, onUpgrade, processing, ctaVariant }) => {
  const { t } = useTranslation();
  const meta = PLAN_META[planId];
  const Icon = meta.icon;
  // pro_trial users see "current" on the free card (they revert to free when trial ends)
  const isCurrent = currentPlan === planId || (currentPlan === 'pro_trial' && planId === 'free');
  const features = t(`pricing.features.${planId}`, { returnObjects: true }) || [];
  const trialLabel = ctaVariant === 'benefit'
    ? t('pricing.start_trial_benefit')
    : t('pricing.start_trial', { days: meta.trial_days });

  return (
    <div
      data-testid={`plan-card-${planId}`}
      className={`relative p-8 rounded-2xl border transition-all ${
        meta.recommended
          ? 'bg-gradient-to-br from-[#2563EB]/10 to-[#3B82F6]/5 border-[#2563EB]/50'
          : 'bg-[#121212] border-white/10'
      } ${isCurrent ? 'ring-2 ring-[#10B981]' : ''}`}
    >
      {meta.recommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#2563EB] rounded-full text-xs font-medium uppercase tracking-[0.2em]">
          {t('pricing.most_popular')}
        </div>
      )}
      {isCurrent && (
        <div className="absolute -top-3 right-4 px-3 py-1 bg-[#10B981] rounded-full text-xs font-medium uppercase tracking-[0.2em]">
          {t('pricing.current_plan_badge')}
        </div>
      )}

      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${meta.color}33` }}
        >
          <Icon size={28} weight="bold" style={{ color: meta.color }} />
        </div>
        <h3 className="text-2xl font-[Outfit] font-bold">{meta.name}</h3>
      </div>

      <div className="my-6">
        <span className="text-5xl font-[Outfit] font-bold">{meta.currency || '€'}{meta.monthly}</span>
        <span className="text-white/40 ml-2">{t('pricing.per_month')}</span>
        {meta.trial_days ? (
          <p className="mt-2 text-xs text-[#10B981] font-medium uppercase tracking-[0.15em]">
            {t('pricing.trial_note', { days: meta.trial_days })}
          </p>
        ) : null}
      </div>

      <ul className="space-y-3 mb-8">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm text-white/80">
            <Check size={18} weight="bold" className="text-[#10B981] flex-shrink-0 mt-0.5" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {planId === 'free' ? (
        <button
          disabled
          data-testid="free-plan-btn"
          className="w-full px-6 py-3 bg-white/5 text-white/40 rounded-lg font-medium cursor-not-allowed"
        >
          {isCurrent ? t('pricing.your_current_plan') : t('pricing.free_label')}
        </button>
      ) : (
        <button
          onClick={() => onUpgrade(planId)}
          data-testid={`upgrade-${planId}-btn`}
          disabled={isCurrent || processing}
          className={`w-full px-6 py-3 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            meta.recommended
              ? 'bg-[#2563EB] hover:bg-[#3B82F6]'
              : 'bg-white/10 hover:bg-white/20'
          }`}
        >
          {isCurrent
            ? t('pricing.your_current_plan')
            : processing
              ? t('pricing.processing')
              : meta.trial_days
                ? trialLabel
                : t('pricing.upgrade_to', { name: meta.name })}
        </button>
      )}
    </div>
  );
};

const Pricing = ({ user }) => {
  const { t } = useTranslation();
  const [currentPlan, setCurrentPlan] = useState('free');
  const [monitorCount, setMonitorCount] = useState(0);
  const [monitorLimit, setMonitorLimit] = useState(3);
  const [trial, setTrial] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [ctaVariant, setCtaVariant] = useState('control');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/experiments/assign/pricing_cta_v1`, { withCredentials: true })
      .then((res) => setCtaVariant(res.data.variant || 'control'))
      .catch(() => {});
  }, []);

  const loadCurrentPlan = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/me`, { withCredentials: true });
      setCurrentPlan(response.data.plan);
      setMonitorCount(response.data.monitor_count);
      setMonitorLimit(response.data.monitor_limit);
      setTrial(response.data.trial);
    } catch {
      // Fallback to free
    }
  }, []);

  useEffect(() => {
    loadCurrentPlan();
  }, [loadCurrentPlan]);

  const handleUpgrade = useCallback(async (planId) => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout-session`,
        { plan_id: planId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (response.data?.url) {
        axios.post(`${BACKEND_URL}/api/experiments/convert`, { experiment_id: 'pricing_cta_v1' }, { withCredentials: true }).catch(() => {});
        window.location.href = response.data.url;
      }
    } catch {
      toast.error(t('pricing.checkout_error'));
      setProcessing(false);
    }
  }, [t]);

  return (
    <DashboardLayout user={user} activePage="/pricing">
      <div className="p-6 max-w-6xl mx-auto" data-testid="pricing-page">
        <div className="text-center mb-12 pt-8">
          <h1 className="text-5xl font-[Outfit] font-bold tracking-tight mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>

          <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-white/5 border border-white/10 rounded-full">
            <Eye size={16} weight="bold" className="text-[#2563EB]" />
            <span className="text-sm text-white/80">
              <Trans i18nKey="pricing.usage" values={{ count: monitorCount, limit: monitorLimit }}>
                You're using <span className="font-bold text-white">x of y</span> monitors
              </Trans>
            </span>
          </div>

          {currentPlan === 'pro_trial' && trial && (
            <div
              data-testid="trial-info-banner"
              className="mt-6 inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#10B981]/20 to-[#2563EB]/10 border border-[#10B981]/30 rounded-xl"
            >
              <Rocket size={20} weight="bold" className="text-[#10B981]" />
              <span className="text-sm text-white">
                <Trans i18nKey="pricing.trial_banner" values={{ days: trial.days_remaining }}>
                  You're on the <strong className="text-[#10B981]">Pro Trial</strong> — days left.
                </Trans>
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {['free', 'pro', 'agency'].map((planId) => (
            <PlanCard
              key={planId}
              planId={planId}
              currentPlan={currentPlan}
              onUpgrade={handleUpgrade}
              processing={processing}
              ctaVariant={ctaVariant}
            />
          ))}
        </div>

        <a href="/credits" data-testid="credits-cta-banner"
          className="mt-8 block p-5 bg-gradient-to-br from-[#F59E0B]/15 via-[#F59E0B]/5 to-transparent border border-[#F59E0B]/35 rounded-2xl hover:border-[#F59E0B]/60 transition group">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] font-bold mb-1">{t('pricing.credits_kicker')}</div>
              <div className="text-lg md:text-xl font-[Outfit] font-bold">{t('pricing.credits_title')}</div>
              <div className="text-xs text-white/60 mt-1">{t('pricing.credits_subtitle')}</div>
            </div>
            <div className="text-[#F59E0B] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
              {t('pricing.credits_cta')} <span aria-hidden>→</span>
            </div>
          </div>
        </a>

        <div className="mt-8 text-center text-sm text-white/40">
          <p>{t('pricing.secure_note')}</p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Pricing;
